import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { excluirAlert } from 'app/services/alerts';
import empresaApi from 'app/services/api/empresaApi';
import { parametrosNotaFiscalApi } from 'app/services/api/parametrosNotaFiscalApi';
import * as Actions from 'app/store/actions';
import { getEmpresaContexto } from 'app/utils/geralUtil';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FaFileContract } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { buildServicoFiscalInitialValue } from 'app/main/venda/components/VendaProdutoServico/cadastro/servicoFiscalVendaUtils';
import { calcularValorLiquidoComRetencoes } from 'app/main/venda/utils/retencaoLiquidoUtils';
import { ContratoAction, ContratoUtil, useContrato } from '../../store';
import ContratoCadastroForm from './ContratoCadastroForm';
import ContratoSchema from './ContratoSchema';

const ContratoCadastroModal = () => {
  const [state, dispatch] = useContrato();
  const { cadastrar, registro, requestParams } = state;
  const [municipioIncidencia, setMunicipioIncidencia] = useState();
  const [exigeImovel, setExigeImovel] = useState();

  const dispatchRedux = useDispatch();

  const showError = (message) => {
    dispatchRedux(Actions.showMessage({ message, variant: 'error' }));
  };

  const MOMENT_HOJE = moment();

  const exibeMsgConfirmacao = (contrato) => {
    // RNE-517
    if (contrato.possuiCobrancaBaixadaOuRemessaGerada || contrato.possuiNotaTransmitida) {
      return (
        !moment(contrato.dataVigenciaFinal).isSame(registro.dataVigenciaFinal) ||
        contrato.valor !== registro.valor ||
        contrato.vencimentoCobranca !== registro.vencimentoCobranca ||
        contrato.formaCobranca !== registro.formaCobranca ||
        contrato.reajusteAutomatico.reajustarPor !== registro.reajusteAutomatico.reajustarPor ||
        contrato.reajusteAutomatico.valorReajuste !== registro.reajusteAutomatico.valorReajuste ||
        contrato.reajusteAutomatico.indiceMonetario !== registro.reajusteAutomatico.indiceMonetario ||
        contrato.reajusteAutomatico.periodicidadeMeses !== registro.reajusteAutomatico.periodicidadeMeses
      );
    }

    return false;
  };

  const calcularSubtotal = (itemServico) =>
    calcularValorLiquidoComRetencoes(itemServico?.valor, itemServico?.servicoFiscal);

  const onSubmitForm = (contrato) => {
    // Verifica se existe algum valor negativo
    const contratoPossuiValorNegativo = contrato.servicos.some((itemServico) => {
      const valor = calcularSubtotal(itemServico);
      if (valor < 0) {
        showError('Não é possível salvar o contrato: o valor do subtotal de um dos serviços está negativo.');
        return true;
      }
      return false;
    });

    // Se não houver valores negativos, procede com a lógica de salvar
    if (!contratoPossuiValorNegativo) {
      if (exibeMsgConfirmacao(contrato)) {
        excluirAlert({
          confirmar: () => {
            ContratoAction.onSubmit(dispatch, contrato, requestParams);
          },
          opcoes: {
            title:
              'As alterações realizadas serão refletidas apenas nas cobranças que ainda não tiveram remessa gerada, baixa e faturamento. As alterações também refletirão nos faturamentos que ainda não foram transmitidos. Deseja continuar?',
            text: '',
          },
        });
      } else {
        ContratoAction.onSubmit(dispatch, contrato, requestParams);
      }
    }
  };

  const getInitialValues = (contrato) => ({
    id: contrato?.id,

    // Dados contrato
    identificador: contrato?.identificador,
    cliente: {
      id: contrato?.cliente?.id,
      nome: contrato?.cliente?.pessoa?.nome,
      emailPrincipal: registro?.cliente?.pessoa?.contatos?.[0]?.email,
      tipoPessoa: contrato?.cliente?.pessoa?.tipoPessoa,
    },
    vendedor: { id: contrato?.vendedor?.id, nome: contrato?.vendedor?.pessoa?.nome },
    imovel: { id: contrato?.imovel?.id, nome: contrato?.imovel?.nome },

    // Campo de controle da RNE-1298. Não é enviado no payload do submit.
    _exigeImovel: !!exigeImovel,
    periodicidade: contrato?.periodicidade ? contrato.periodicidade : 'MENSAL',
    dataVigenciaInicial: contrato?.dataVigenciaInicial ? moment(contrato.dataVigenciaInicial) : undefined,
    dataVigenciaFinal: contrato?.dataVigenciaFinal ? moment(contrato.dataVigenciaFinal) : undefined,
    periodoVigencia:
      contrato?.periodicidade === 'ANUAL'
        ? contrato?.vigenciaIndeterminada
          ? '-'
          : 'Anual'
        : contrato?.dataVigenciaInicial && contrato?.dataVigenciaFinal
        ? ContratoUtil.calcularPeriodoVigencia(
            moment(contrato?.dataVigenciaInicial),
            moment(contrato?.dataVigenciaFinal)
          )
        : '-',
    vigenciaIndeterminada: contrato?.vigenciaIndeterminada || false,
    servicos: contrato?.servicos
      ? contrato.servicos.map((itemServico) => ({
          ...itemServico,
          servicoFiscal: buildServicoFiscalInitialValue(
            itemServico.servico?.fiscal || {},
            itemServico.servicoFiscal
          ),
        }))
      : [
          {
            servico: {},
            servicoFiscal: {},
          },
        ],
    categorias: contrato?.categorias
      ? contrato.categorias
      : [{ id: undefined, classe: {}, valorBruto: undefined, valorBrutoContratado: undefined }],
    valor: contrato?.valor,
    valorContratado: contrato?.valorContratado || contrato?.valor,
    municipioIncidencia: contrato?.municipioIncidencia || municipioIncidencia,
    informacoesComplementares: contrato?.informacoesComplementares,

    // Informações de pagamento
    vencimentoCobranca: contrato?.vencimentoCobranca || 1,
    formaCobranca: contrato?.formaCobranca || 'BOLETO',
    envioAutomaticoCobranca: contrato?.envioAutomaticoCobranca || false,
    enviarCobrancaEmail: contrato?.enviarCobrancaEmail || false,
    enviarNotaFiscalEmail: contrato?.enviarNotaFiscalEmail || false,
    emailsCliente: contrato?.emailsCliente || [{ email: '', principal: true }],
    recebimentoDiferentePrimeiraCobranca: contrato?.recebimentoDiferentePrimeiraCobranca ?? false,
    formaRecebimentoPrimeiraCobranca: contrato?.formaRecebimentoPrimeiraCobranca ?? null,
    dataVencimentoPrimeiraCobranca: contrato?.dataVencimentoPrimeiraCobranca ?? null,

    // Adesão
    adesao: {
      id: contrato?.adesao?.id,
      cobrarAdesao: !!contrato?.adesao,
      servico: contrato?.adesao?.servico ? { ...contrato?.adesao?.servico } : undefined,
      categoria: contrato?.adesao?.categoria ? { ...contrato?.adesao?.categoria } : undefined,
      valor: contrato?.adesao?.valor,
      subtotal: contrato?.adesao?.subtotal,
      formaCobranca: contrato?.adesao?.formaCobranca,
      dataVencimento: contrato?.adesao?.dataVencimento,
      isCobrancaBaixadaOuRemessaGerada: contrato?.adesao?.isCobrancaBaixadaOuRemessaGerada,
      servicoFiscal: buildServicoFiscalInitialValue(
        contrato?.adesao?.servico?.fiscal || {},
        contrato?.adesao?.servicoFiscal
      ),
    },

    // Anexo
    anexos: contrato?.anexos || undefined,

    // Desabilitar campos
    possuiCobrancaBaixadaOuRemessaGerada: contrato?.possuiCobrancaBaixadaOuRemessaGerada,

    possuiCobrancaGerada: contrato?.possuiCobrancaGerada || false,
    reajusteAutomatico: {
      id: contrato?.reajusteAutomatico?.id || undefined,
      aplicarReajusteAutomatico: contrato?.reajusteAutomatico?.aplicarReajusteAutomatico || false,
      reajustarPor: contrato?.reajusteAutomatico?.reajustarPor || 'INDICE',
      valorReajuste: contrato?.reajusteAutomatico?.valorReajuste || 0,
      indiceMonetario: contrato?.reajusteAutomatico?.indiceMonetario || 'IGP_DI',
      mesBaseReajuste: contrato?.reajusteAutomatico?.mesBaseReajuste || MOMENT_HOJE,
      periodicidadeMeses: contrato?.reajusteAutomatico?.periodicidadeMeses || 12,
      bloquearEdicao: contrato?.reajusteAutomatico?.bloquearEdicao || false,
    },

    possuiNotaTransmitida: contrato?.possuiNotaTransmitida,
  });

  const formik = useFormik({
    initialValues: getInitialValues(registro),
    onSubmit: onSubmitForm,
    validationSchema: ContratoSchema,
    validateOnBlur: true,
  });

  useEffect(() => {
    buscarParametrosNF();
    buscarEmpresa();
    if (cadastrar) {
      formik.resetForm({
        values: getInitialValues(registro),
      });
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadastrar]);

  // A empresa é carregada de forma assíncrona; se ela chegar depois da abertura do modal,
  // o _exigeImovel precisa ser reaplicado, senão a seção do imóvel não aparece e o contrato
  // é enviado sem imovelId (RNE-1298).
  useEffect(() => {
    if (!cadastrar || exigeImovel === undefined) {
      return;
    }

    if (formik.dirty) {
      // Já há alteração do usuário: não pode resetar, senão o initialValues é reescrito,
      // o dirty se perde e o Cancelar deixa de pedir confirmação.
      formik.setFieldValue('_exigeImovel', exigeImovel);
    } else {
      formik.resetForm({
        values: getInitialValues(registro),
      });
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exigeImovel]);

  const handleCancelar = () => {
    ContratoAction.fecharModalCadastro(dispatch, formik.dirty);
  };

  const buscarParametrosNF = async () => {
    try {
      const params = await parametrosNotaFiscalApi().getParametrosNotaFiscalEmpresa();
      setMunicipioIncidencia(params?.data?.municipioIncidencia);
    } catch (err) {
      console.log(err);
    }
  };

  // RNE-1298
  const buscarEmpresa = async () => {
    try {
      const response = await empresaApi.buscar(getEmpresaContexto());
      const codigoOperacaoIbsCbs = response?.data?.codigoOperacaoIbsCbs?.codigo;

      setExigeImovel(ContratoUtil.exigeImovelRelacionado(codigoOperacaoIbsCbs));
    } catch (err) {
      console.log(err);
      setExigeImovel(false);
    }
  };

  return (
    <SimpleDialog fullScreen open={cadastrar} onClose={handleCancelar} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Cadastro de contrato',
            icon: <FaFileContract />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={<ContratoCadastroForm />}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ContratoCadastroModal;
